import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Map, { Layer, Marker, Source } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { useAlarms } from "../useAlarms";
import { useCoordinates } from "./common";
import { useStormData } from "../storm/useStormData";
import { demoRadar, isDemoData, isPreviewStill } from "../boardData";
import { DEMO_MAP_TOKEN, demoMapStyle } from "../demo/demoBasemap";
import DemoPlaces from "../demo/DemoPlaces";
import {
  REPORT_STYLE,
  alertStyle,
  circlePolygon,
  clockTime,
  compass,
  filterReports,
  radarFrames,
  reportKind,
  threatOf,
} from "../storm/stormUi";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
// Previews draw an offline basemap and need no real token.
const mapToken = () => (isDemoData() ? DEMO_MAP_TOKEN : MAPBOX_TOKEN);

const MAP_STYLES = {
  dark: "mapbox://styles/mapbox/dark-v11",
  satellite: "mapbox://styles/mapbox/satellite-streets-v12",
  light: "mapbox://styles/mapbox/light-v11",
};

const FRAME_MS = 550; // time on each past radar frame
const HOLD_MS = 2600; // time held on the live frame before the loop restarts

function alarmLatLng(a) {
  const lat = Number(a.Latitude ?? a.latitude ?? a.lat);
  const lng = Number(a.Longitude ?? a.longitude ?? a.lng ?? a.long);
  return Number.isFinite(lat) && Number.isFinite(lng) && (lat || lng) ? { lat, lng } : null;
}

// The Storm Center map: IEM NEXRAD reflectivity looped over the last 50
// minutes, NWS warning polygons, every tracked storm cell with its projected
// path (15-minute ticks out to an hour), NWS/mPING storm reports, the
// agency's active alarms, and a range ring around the station.
const StormMapWidget = ({ config = {}, agency, board }) => {
  const station = useCoordinates(agency, board);
  // The map range, in miles: storms and reports are tracked within it, the
  // range ring is drawn at it, and the map is zoomed so the ring fills it.
  const radiusMi = Math.min(250, Math.max(10, Number(config.radiusMi) || 75));
  const { data, error, loading } = useStormData({ agency, lat: station.lat, lng: station.lng, radiusMi });
  const alarms = useAlarms(config.showAlarms === false ? null : agency);

  // Radar frames re-key every 5 minutes so the loop always shows fresh images.
  const [clock, setClock] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setClock(Date.now()), 60 * 1000);
    return () => clearInterval(t);
  }, []);
  const bucket = Math.floor(clock / (5 * 60 * 1000));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const frames = useMemo(() => radarFrames({ loop: config.radarLoop !== false && !isPreviewStill(), now: clock }), [bucket, config.radarLoop]);

  const [frameIdx, setFrameIdx] = useState(frames.length - 1);
  useEffect(() => {
    if (frames.length < 2) {
      setFrameIdx(0);
      return undefined;
    }
    let idx = 0;
    let timer;
    const step = () => {
      setFrameIdx(idx);
      const live = idx === frames.length - 1;
      idx = live ? 0 : idx + 1;
      timer = setTimeout(step, live ? HOLD_MS : FRAME_MS);
    };
    step();
    return () => clearTimeout(timer);
  }, [frames]);

  const opacity = Math.min(1, Math.max(0.2, Number(config.radarOpacity) || 0.75));
  const frameMinutes = frames[Math.min(frameIdx, frames.length - 1)]?.minutesAgo ?? 0;
  const demoEchoes = useMemo(() => (isDemoData() ? demoRadar(frameMinutes) : null), [frameMinutes]);
  const reports = useMemo(
    () => (config.showReports === false ? [] : filterReports(data?.reports, { hours: config.reportHours || 3 })),
    [data, config.showReports, config.reportHours]
  );
  const cells = useMemo(() => (config.showTracks === false ? [] : data?.cells || []), [data, config.showTracks]);

  const warningsGeo = useMemo(() => {
    const features = (data?.alerts || [])
      .filter((a) => a.geometry)
      .map((a) => ({
        type: "Feature",
        geometry: a.geometry,
        properties: { color: alertStyle(a.event).color, rank: alertStyle(a.event).rank, watch: /Watch/.test(a.event) ? 1 : 0 },
      }))
      .sort((a, b) => a.properties.rank - b.properties.rank);
    return { type: "FeatureCollection", features };
  }, [data]);

  const tracksGeo = useMemo(
    () => ({
      type: "FeatureCollection",
      features: cells
        .filter((c) => Array.isArray(c.track) && c.track.length > 1)
        .flatMap((c) => [
          {
            type: "Feature",
            geometry: { type: "LineString", coordinates: c.track },
            properties: { color: threatOf(c.threat).color },
          },
          ...c.track.slice(1).map((pt) => ({
            type: "Feature",
            geometry: { type: "Point", coordinates: pt },
            properties: { color: threatOf(c.threat).color },
          })),
        ]),
    }),
    [cells]
  );

  const ringGeo = useMemo(
    () =>
      Number.isFinite(station.lat) && Number.isFinite(station.lng)
        ? { type: "Feature", geometry: circlePolygon(station.lat, station.lng, radiusMi), properties: {} }
        : null,
    [station.lat, station.lng, radiusMi]
  );

  // Frame the map on the range ring (re-run when the range or station changes
  // and when the widget resizes). Leaves room for the top bar and legend.
  const mapRef = useRef(null);
  const fitRange = useCallback(() => {
    const map = mapRef.current;
    if (!map || !ringGeo) return;
    const ring = ringGeo.geometry.coordinates[0];
    const lngs = ring.map((p) => p[0]);
    const lats = ring.map((p) => p[1]);
    const h = map.getContainer().clientHeight || 600;
    const w = map.getContainer().clientWidth || 800;
    map.fitBounds(
      [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
      {
        padding: { top: Math.round(h * 0.09), bottom: Math.round(h * 0.08), left: Math.round(w * 0.02), right: Math.round(w * 0.02) },
        duration: 0,
      }
    );
  }, [ringGeo]);
  useEffect(() => {
    fitRange();
  }, [fitRange]);

  if (station.lat == null || station.lng == null) {
    return (
      <div className="sb-card sb-storm">
        <div className="sb-empty">
          <div className="sb-empty-icon">🌪</div>
          <div>Set this board's location to track storms.</div>
        </div>
      </div>
    );
  }

  const current = frames[Math.min(frameIdx, frames.length - 1)];
  const src = data?.sources || {};
  const degraded = error || (data && (!src.nexrad?.ok || !src.nws?.ok));
  const approaching = (data?.cells || []).filter((c) => c.approaching).sort((a, b) => (a.etaMin ?? 1e9) - (b.etaMin ?? 1e9))[0];

  return (
    <div className="sb-card sb-storm">
      {mapToken() ? (
        <Map
          reuseMaps
          mapboxAccessToken={mapToken()}
          ref={mapRef}
          initialViewState={{ latitude: station.lat, longitude: station.lng, zoom: 8 }}
          onLoad={fitRange}
          onResize={fitRange}
          mapStyle={isDemoData() ? demoMapStyle() : MAP_STYLES[config.mapStyle] || MAP_STYLES.dark}
          style={{ width: "100%", height: "100%" }}
          attributionControl={false}
          interactive={false}
        >
          <DemoPlaces />

          {/* Preview: sample reflectivity drawn from the demo storm cells. */}
          {demoEchoes && (
            <Source id="storm-demo-radar" type="geojson" data={demoEchoes}>
              <Layer
                id="storm-demo-radar-heat"
                type="heatmap"
                paint={{
                  "heatmap-weight": ["get", "w"],
                  "heatmap-intensity": ["interpolate", ["linear"], ["zoom"], 5, 0.45, 8, 0.6, 11, 0.9],
                  "heatmap-radius": ["interpolate", ["exponential", 2], ["zoom"], 5, 11, 6, 15, 8, 26, 10, 60],
                  "heatmap-opacity": opacity,
                  "heatmap-color": [
                    "interpolate", ["linear"], ["heatmap-density"],
                    0, "rgba(0,0,0,0)",
                    0.06, "rgba(0,150,60,0.3)",
                    0.2, "rgba(0,190,0,0.7)",
                    0.4, "#e3e000",
                    0.56, "#ff9a00",
                    0.72, "#ff2a00",
                    0.88, "#c8008c",
                    1, "#a0009a",
                  ],
                }}
              />
            </Source>
          )}

          {/* All frames stay mounted so their tiles are cached; only one is visible. */}
          {!demoEchoes && frames.map((f, i) => (
            <Source key={f.id} id={f.id} type="raster" tiles={f.tiles} tileSize={256} maxzoom={12}>
              <Layer
                id={`${f.id}-layer`}
                type="raster"
                paint={{
                  "raster-opacity": i === frameIdx ? opacity : 0,
                  "raster-opacity-transition": { duration: 0 },
                  "raster-fade-duration": 0,
                }}
              />
            </Source>
          ))}

          {ringGeo && (
            <Source id="storm-ring" type="geojson" data={ringGeo}>
              <Layer
                id="storm-ring-line"
                type="line"
                paint={{ "line-color": "#93c5fd", "line-opacity": 0.35, "line-width": 1.5, "line-dasharray": [2, 3] }}
              />
            </Source>
          )}

          {config.showWarnings !== false && (
            <Source id="storm-warnings" type="geojson" data={warningsGeo}>
              <Layer
                id="storm-warnings-fill"
                type="fill"
                paint={{ "fill-color": ["get", "color"], "fill-opacity": ["case", ["==", ["get", "watch"], 1], 0.06, 0.16] }}
              />
              <Layer
                id="storm-warnings-line"
                type="line"
                paint={{ "line-color": ["get", "color"], "line-width": ["case", ["==", ["get", "watch"], 1], 1.5, 3] }}
              />
            </Source>
          )}

          <Source id="storm-tracks" type="geojson" data={tracksGeo}>
            <Layer
              id="storm-tracks-line"
              type="line"
              filter={["==", ["geometry-type"], "LineString"]}
              paint={{ "line-color": ["get", "color"], "line-width": 2.5, "line-dasharray": [1.5, 1.5] }}
            />
            <Layer
              id="storm-tracks-ticks"
              type="circle"
              filter={["==", ["geometry-type"], "Point"]}
              paint={{ "circle-radius": 3.5, "circle-color": "#0b1220", "circle-stroke-color": ["get", "color"], "circle-stroke-width": 2 }}
            />
          </Source>

          {cells.map((c) => {
            const t = threatOf(c.threat);
            return (
              <Marker key={c.id} latitude={c.lat} longitude={c.lng} anchor="center">
                <div className={`sb-storm-cell t-${c.threat}${c.approaching ? " approaching" : ""}`} style={{ "--c": t.color }}>
                  <span className="sb-storm-cell-arrow" style={{ transform: `rotate(${c.headingDeg || 0}deg)` }}>▲</span>
                  {(c.threat === "tornado" || c.threat === "severe" || c.approaching) && (
                    <span className="sb-storm-cell-tag">
                      {c.stormId || c.nexrad}
                      {c.approaching && c.etaMin != null ? ` · ${Math.round(c.etaMin)} min` : ""}
                    </span>
                  )}
                </div>
              </Marker>
            );
          })}

          {reports.map((r) => {
            const s = REPORT_STYLE[reportKind(r.type)];
            return (
              <Marker key={r.id} latitude={r.lat} longitude={r.lng} anchor="center">
                <div className={`sb-storm-report${r.source === "mping" ? " mping" : ""}`} style={{ "--c": s.color }} title={r.type}>
                  {s.glyph}
                </div>
              </Marker>
            );
          })}

          {config.showAlarms !== false &&
            alarms.map((a) => {
              const loc = alarmLatLng(a);
              if (!loc) return null;
              return (
                <Marker key={`alarm-${a.id}`} latitude={loc.lat} longitude={loc.lng} anchor="center">
                  <div className="sb-storm-alarm">
                    <div className="sb-alarm-marker" />
                    <span>{a.CallType || a.callType || a.DispatchCode || "Alarm"}</span>
                  </div>
                </Marker>
              );
            })}

          <Marker latitude={station.lat} longitude={station.lng} anchor="center">
            <div className="sb-station-marker" />
          </Marker>
        </Map>
      ) : (
        <div className="sb-alarmmap-nomap">
          <div className="sb-empty">
            <div>Map unavailable — no Mapbox token configured.</div>
          </div>
        </div>
      )}

      <div className="sb-storm-topbar">
        <span className={`sb-storm-radar${current?.minutesAgo === 0 ? " live" : ""}`}>
          <i /> RADAR {current?.minutesAgo === 0 ? "NOW" : `−${current?.minutesAgo} min`}
        </span>
        {frames.length > 1 && (
          <span className="sb-storm-scrub">
            {frames.map((f, i) => (
              <b key={f.id} className={i <= frameIdx ? "on" : ""} />
            ))}
          </span>
        )}
        <span className="sb-storm-range">{radiusMi} mi</span>
      </div>

      {approaching && (
        <div className="sb-storm-banner" style={{ "--c": threatOf(approaching.threat).color }}>
          <b>{threatOf(approaching.threat).label}</b> cell {approaching.stormId || ""} moving{" "}
          {compass(approaching.headingDeg)} at {Math.round(approaching.speedMph || 0)} mph
          {approaching.etaMin != null ? ` — near the station in ~${Math.round(approaching.etaMin)} min` : ""}
        </div>
      )}

      <div className="sb-storm-legend">
        <span><i className="dot" style={{ background: "#d946ef" }} /> Tornadic</span>
        <span><i className="dot" style={{ background: "#ef4444" }} /> Severe</span>
        <span><i className="dot" style={{ background: "#f97316" }} /> Strong</span>
        <span><i className="dash" /> Projected track (15-min ticks)</span>
        {config.showReports !== false && <span><i className="sq" /> Storm reports</span>}
        {config.showAlarms !== false && <span><i className="dot" style={{ background: "#ef4444", boxShadow: "0 0 0 3px rgba(239,68,68,.35)" }} /> Active alarms</span>}
        <span className="sb-storm-updated">
          {loading && !data ? "Loading storm data…" : data ? `Updated ${clockTime(data.generatedAt)}` : ""}
          {degraded ? " · some feeds unavailable" : ""}
        </span>
      </div>
    </div>
  );
};

export default StormMapWidget;
